import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaRobot } from "react-icons/fa";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";

import { CommunicationManager } from "../api/communication.js";
import { fetchFontAsset } from "../utils";
import undiz_logo from "../assets/undiz-logo.svg";
import { useGTagScript, useUpdateImages } from "../utils/hooks.js";

// Create context
const AppContext = createContext({});

const AppContextProvider = ({ children, config }) => {
  const [sessionId, setSessionId] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [communicationManager, setCommunicationManager] = useState(null);
  const [storefrontApiClient, setStorefrontApiClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState("");
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [dislikeReason, setDislikeReason] = useState("");
  const [isTextInputQuestionActive, setIsTextInputQuestionActive] = useState(false);
  const [textQuestionAnswer, setTextQuestionAnswer] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [theme, setTheme] = useState(null);
  const [isWaitingForRes, setWaitingForRes] = useState(false);
  const [connectionRetries, setConnectionRetries] = useState(0);
  const fireTrackingEvent = useGTagScript({ theme });

  const [clientIcon, setClientIcon] = useState(
    <FaRobot
      style={{
        width: "3vh",
        height: "3vh",
        backgroundColor: "#c2c2c2",
        borderRadius: "50%",
      }}
    />
  );

  const dislikeReasonRef = useRef(null);
  const commManager = useRef(null);
  const connectionCheckInterval = useRef(null);

  const persistSession = (sessionId, tenant, theme) => {
    console.log("Persisting session data:", { sessionId, tenant, theme });
    try {
      if (sessionId) localStorage.setItem("sessionId", sessionId);
      if (tenant) localStorage.setItem("tenant", JSON.stringify(tenant));
      if (theme) localStorage.setItem("theme", JSON.stringify(theme));
    } catch (error) {
      console.error("Error persisting session:", error);
    }
  };

  const loadPersistedSession = () => {
    try {
      const savedSessionId = localStorage.getItem("sessionId");
      const savedTenant = JSON.parse(localStorage.getItem("tenant") || "null");
      const savedTheme = JSON.parse(localStorage.getItem("theme") || "null");
      return { savedSessionId, savedTenant, savedTheme };
    } catch (error) {
      console.error("Error loading persisted session:", error);
      return { savedSessionId: null, savedTenant: null, savedTheme: null };
    }
  };

  const loadMessagesFromLocalStorage = () => {
    try {
      const savedMessages = JSON.parse(localStorage.getItem("messages") || "[]");
      setMessages(savedMessages);
    } catch (error) {
      console.error("Error loading messages from localStorage:", error);
      setMessages([]);
    }
  };

  const persistMessagesToLocalStorage = (messages) => {
    try {
      localStorage.setItem("messages", JSON.stringify(messages));
    } catch (error) {
      console.error("Error persisting messages:", error);
    }
  };

  const clearInvalidSession = () => {
    console.log("Clearing invalid session data");
    try {
      localStorage.removeItem("sessionId");
      localStorage.removeItem("tenant");
      localStorage.removeItem("theme");
      localStorage.removeItem("messages");
    } catch (error) {
      console.error("Error clearing session data:", error);
    }
    setSessionId(null);
    setTenant(null);
    setTheme(null);
    setMessages([]);
  };

  const validateSession = (savedSessionId, savedTenant, savedTheme) => {
    // Check if session data is complete and valid
    const isValidSession = 
      savedSessionId &&
      savedTenant &&
      savedTenant.shopify_shop_url &&
      savedTheme &&
      typeof savedTenant === 'object' &&
      typeof savedTheme === 'object';

    if (!isValidSession) {
      console.log("Invalid session detected, clearing data");
      clearInvalidSession();
      return false;
    }

    return true;
  };

  useEffect(() => {
    const initializeCommunication = async () => {
      try {
        setError("");
        const { savedSessionId, savedTenant, savedTheme } = loadPersistedSession();
        loadMessagesFromLocalStorage();

        commManager.current = new CommunicationManager({
          token: config.token,
          api_base: config.api_base,
        });

        setCommunicationManager(commManager.current);

        const isValidSession = validateSession(savedSessionId, savedTenant, savedTheme);

        console.log("Loaded session data:", {
          savedSessionId,
          savedTenant,
          savedTheme,
          isValid: isValidSession,
        });

        // Check if we have existing valid session data
        if (isValidSession) {
          // Set the loaded session
          setSessionId(savedSessionId);
          setTenant(savedTenant);
          setTheme(savedTheme);

          try {
            // Reinitialize the Storefront API client
            if (savedTenant.shopify_storefront_access_token) {
              setStorefrontApiClient(
                createStorefrontApiClient({
                  storeDomain: savedTenant.shopify_shop_url,
                  apiVersion: "2024-07",
                  publicAccessToken: savedTenant.shopify_storefront_access_token,
                })
              );
            }
          } catch (error) {
            console.error("Error initializing Shopify client:", error);
            clearInvalidSession();
            await initializeNewSession();
          }
        } else {
          await initializeNewSession();
        }

        setLoadingInitial(false);
      } catch (error) {
        console.error("Initialization error:", error);
        setError("Failed to initialize. Please refresh the page.");
        clearInvalidSession();
        setLoadingInitial(false);
      }
    };

    const initializeNewSession = async () => {
      try {
        // Initialize a new session
        await commManager.current.initialize();
        const newSessionId = commManager.current.session_id;
        const newTenant = commManager.current.tenant;
        const newTheme = commManager.current.theme;

        if (!newTenant || !newTheme) {
          throw new Error("Failed to initialize tenant or theme data");
        }

        try {
          // Initialize Storefront API client if we have the token
          if (newTenant.shopify_storefront_access_token) {
            setStorefrontApiClient(
              createStorefrontApiClient({
                storeDomain: newTenant.shopify_shop_url,
                apiVersion: "2024-07",
                publicAccessToken: newTenant.shopify_storefront_access_token,
              })
            );
          }
        } catch (error) {
          console.error("Error initializing Shopify client:", error);
          // Continue without Shopify client
        }

        // Set state
        setSessionId(newSessionId);
        setTenant(newTenant);
        setTheme(newTheme);

        // Persist new session data
        persistSession(newSessionId, newTenant, newTheme);
      } catch (error) {
        console.error("Failed to initialize new session:", error);
        setError("Failed to create new session. Please refresh the page.");
        throw error;
      }
    };

    initializeCommunication();

    return () => {
      if (commManager.current) {
        commManager.current.disconnect();
      }
      if (connectionCheckInterval.current) {
        clearInterval(connectionCheckInterval.current);
      }
    };
  }, [config.token, config.api_base]);

  const connectWebSocket = () => {
    if (!commManager.current) {
      console.error("Communication manager not initialized");
      return;
    }

    try {
      commManager.current.connectWebSocket(sessionId);

      commManager.current.onMessage = (message) => {
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages, message];
          persistMessagesToLocalStorage(updatedMessages);
          return updatedMessages;
        });
        setWaitingForRes(false);
      };

      commManager.current.onConnect = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
        setError("");
        setConnectionRetries(0);
      };

      commManager.current.onDisconnect = () => {
        console.log("WebSocket disconnected");
        setIsConnected(false);
        setConnectionRetries(prev => prev + 1);
      };

      commManager.current.onError = (error) => {
        console.error("WebSocket error:", error);
        setIsConnected(false);
        if (connectionRetries >= 3) {
          setError("Connection failed. Please try refreshing the page.");
        }
      };

      // Start periodic connection health check
      if (connectionCheckInterval.current) {
        clearInterval(connectionCheckInterval.current);
      }
      
      connectionCheckInterval.current = setInterval(() => {
        if (commManager.current) {
          const state = commManager.current.getConnectionState();
          if (state === 'failed' && connectionRetries < 3) {
            console.log("Connection health check: attempting recovery");
            commManager.current.forceReconnect();
          }
        }
      }, 10000); // Check every 10 seconds

    } catch (error) {
      console.error("Error connecting WebSocket:", error);
      setError("Failed to connect. Please try again.");
    }
  };

  const loadedFonts = useRef(new Set());

  useEffect(() => {
    if (!theme) {
      return;
    }

    const fontURL = theme.font_url;

    if (!fontURL) {
      return;
    }

    if (loadedFonts.current.has(fontURL)) {
      return;
    }

    loadedFonts.current.add(fontURL);
    try {
      fetchFontAsset(fontURL);
    } catch (error) {
      console.error("Error loading font:", error);
    }
  }, [theme]);

  useUpdateImages({ theme });

  const handleDislikeReasonInput = (e) => {
    setDislikeReason(e.target.value);
  };

  const expandCompressChat = () => {
    setIsExpanded(!isExpanded);
  };

  const setTextQuestionActive = (status) => {
    setIsTextInputQuestionActive(status);
  };

  const disableForm = (disabled) => {
    setIsFormDisabled(disabled);
  };

  const sendMessage = (message) => {
    if (!commManager.current) {
      console.error("Communication manager not available");
      setError("Not connected. Please refresh the page.");
      return;
    }

    try {
      commManager.current.sendMessage(message);
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message. Please try again.");
    }
  };

  const addMessage = (message) => {
    const updatedMessages = [...messages, message];
    setMessages(updatedMessages);
    persistMessagesToLocalStorage(updatedMessages);
  };

  const scrollRef = useRef();
  const onScroll = async ({ target }) => {};

  const onReset = () => {
    console.log("Resetting chat session");
    setMessages([]);
    setError("");
    setConnectionRetries(0);
    
    if (commManager.current) {
      commManager.current.reset();
    }
    
    // Clear messages from localStorage
    try {
      localStorage.removeItem("messages");
    } catch (error) {
      console.error("Error clearing messages:", error);
    }
  };

  // Enhanced recovery function
  const recoverConnection = () => {
    console.log("Attempting connection recovery");
    setError("");
    
    if (commManager.current) {
      commManager.current.forceReconnect();
    } else {
      connectWebSocket();
    }
  };

  return (
    <AppContext.Provider
      value={{
        messages,
        loadingInitial,
        error,
        connectWebSocket,
        sendMessage,
        addMessage,
        isConnected,
        communicationManager,
        storefrontApiClient,
        scrollRef,
        onScroll,
        sessionId,
        tenant,
        theme,
        isExpanded,
        expandCompressChat,
        isTextInputQuestionActive,
        setTextQuestionActive,
        setTextQuestionAnswer,
        textQuestionAnswer,
        clientIcon,
        isFormDisabled,
        disableForm,
        onReset,
        dislikeReason,
        dislikeReasonRef,
        handleDislikeReasonInput,
        persistMessagesToLocalStorage,
        isWaitingForRes,
        setWaitingForRes,
        fireTrackingEvent,
        config,
        connectionRetries,
        recoverConnection,
        clearInvalidSession,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const useAppContext = () => useContext(AppContext);

export { AppContext as default, AppContextProvider, useAppContext };